import React from "react";
import type { SectionComponentProps } from "../../registry.js";
import type { SocialPlatform } from "@repo/backend/schema/siteConfig";

interface SocialEntry {
  platform: SocialPlatform;
  url: string;
  label?: string;
  handle?: string;
}

interface SocialLinksProps {
  headline?: string;
  subheadline?: string;
  platforms?: SocialEntry[];
}

const PLATFORM_META: Record<
  SocialPlatform,
  { label: string; color: string; bg: string }
> = {
  instagram: { label: "Instagram", color: "#E1306C", bg: "#fdf2f8" },
  facebook: { label: "Facebook", color: "#1877F2", bg: "#eff6ff" },
  tiktok: { label: "TikTok", color: "#010101", bg: "#f9f9f9" },
  twitter: { label: "X", color: "#000000", bg: "#f9f9f9" },
  youtube: { label: "YouTube", color: "#FF0000", bg: "#fff1f1" },
  yelp: { label: "Yelp", color: "#D32323", bg: "#fff1f1" },
  google: { label: "Google", color: "#4285F4", bg: "#eff6ff" },
  linkedin: { label: "LinkedIn", color: "#0A66C2", bg: "#eff8ff" },
  pinterest: { label: "Pinterest", color: "#E60023", bg: "#fff1f2" },
};

// Full-size platform wordmarks / icons
function PlatformIcon({ platform }: { platform: SocialPlatform }) {
  switch (platform) {
    case "instagram":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
          <path d="M12 2c2.717 0 3.056.01 4.122.06 2.908.134 4.683 1.912 4.818 4.818.05 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.135 2.902-1.906 4.684-4.818 4.818-1.066.05-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-2.906-.134-4.683-1.912-4.818-4.818C2.01 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122C2.195 4.914 3.97 3.134 6.878 3c1.066-.05 1.405-.06 4.122-.06m0-1.802c-2.765 0-3.11.012-4.19.061-3.87.178-6.015 2.32-6.192 6.192C1.612 8.89 1.6 9.235 1.6 12c0 2.765.012 3.11.061 4.19.178 3.868 2.316 6.013 6.192 6.192 1.08.049 1.425.061 4.19.061 2.765 0 3.11-.012 4.19-.061 3.863-.178 6.015-2.32 6.192-6.192.048-1.08.06-1.425.06-4.19 0-2.765-.012-3.11-.06-4.19-.174-3.862-2.32-6.013-6.192-6.192C15.11.212 14.765.198 12 .198zm0 3.055a5.747 5.747 0 100 11.494A5.747 5.747 0 0012 3.253zm0 9.478a3.731 3.731 0 110-7.462 3.731 3.731 0 010 7.462zm7.318-9.709a1.343 1.343 0 10-2.686 0 1.343 1.343 0 002.686 0z" />
        </svg>
      );
    case "facebook":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
          <path d="M24 12.073C24 5.406 18.627 0 12 0S0 5.406 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.791-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.514c-1.491 0-1.956.93-1.956 1.886v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
        </svg>
      );
    case "tiktok":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
        </svg>
      );
    case "twitter":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "youtube":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
          <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    case "yelp":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-.52 5.466c.49-.2 1.042.07 1.168.574l1.365 5.478c.065.264-.17.505-.44.447l-5.428-1.183c-.508-.11-.738-.698-.456-1.133l3.791-4.183zm-5.6 8.054l2.865-1.728c.234-.141.526-.02.615.239l1.745 5.247c.17.51-.268 1.02-.798.925l-4.538-.82c-.53-.096-.787-.7-.499-1.163l.61-.7zm4.12 5.914l-.487-3.324c-.04-.27.186-.503.456-.483l5.587.42c.523.04.796.638.516 1.085l-2.538 4.007c-.296.467-.945.484-1.264.034l-2.27-1.739zm6.986-1.09l-3.197-1.228c-.26-.1-.353-.425-.18-.65l3.565-4.505c.355-.448 1.026-.327 1.215.222l1.42 4.152c.186.546-.195 1.1-.763 1.104l-2.06-.095z" />
        </svg>
      );
    case "google":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
          <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    case "pinterest":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
          <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
        </svg>
      );
  }
}

const DEMO_PLATFORMS: SocialEntry[] = [
  { platform: "instagram", url: "#", handle: "@thegrateful.table" },
  { platform: "facebook", url: "#", handle: "The Grateful Table" },
  { platform: "yelp", url: "#", handle: "The Grateful Table SF" },
  { platform: "google", url: "#", handle: "Google Reviews" },
];

export function SocialLinks({ props }: SectionComponentProps) {
  const {
    headline = "Follow Our Story",
    subheadline = "Stay connected for seasonal menus, events, and behind-the-scenes moments.",
    platforms = DEMO_PLATFORMS,
  } = props as SocialLinksProps;

  return (
    <section className="py-20 px-6 bg-background" id="social">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-3">{headline}</h2>
          {subheadline && (
            <p className="text-muted-foreground text-base max-w-md mx-auto">{subheadline}</p>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {platforms.map(({ platform, url, label, handle }) => {
            const meta = PLATFORM_META[platform];
            return (
              <a
                key={platform}
                href={url}
                target={url !== "#" ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-stagger-child
                className="group flex flex-col items-center gap-3 rounded-2xl border border-border p-6 hover:border-primary/30 hover:shadow-md transition-all duration-200 text-center"
              >
                <span style={{ color: meta.color }} className="transition-transform group-hover:scale-110 duration-200">
                  <PlatformIcon platform={platform} />
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {label ?? meta.label}
                </span>
                {handle && (
                  <span className="text-xs text-muted-foreground truncate max-w-full">
                    {handle}
                  </span>
                )}
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
